import React from "react"
import { Box, ButtonBase, Stack } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { colors, typeScale, typography } from "../theme/tokens"
import { VIRA_CARD_RADIUS } from "../atoms/ViraCard"

export type SegmentedOption<T extends string = string> = {
  value: T
  label: string
  disabled?: boolean
}

export type SegmentedToggleProps<T extends string = string> = {
  options: SegmentedOption<T>[]
  value: T
  onChange?: (value: T) => void
  fullWidth?: boolean
  /** Accessible name for the control */
  "aria-label"?: string
}

/**
 * Segmented control matching ContentTabs chrome (Scores / Transcript / …).
 * Soft surface track, paper selected pill, shared typeScale.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  fullWidth = true,
  "aria-label": ariaLabel = "Options",
}: SegmentedToggleProps<T>) {
  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={(theme) => {
        const border = theme.palette.vira?.border ?? colors.vira.border
        const soft = theme.palette.vira?.softSurface ?? colors.vira.softSurface
        return {
          display: "inline-flex",
          alignItems: "stretch",
          alignSelf: fullWidth ? "stretch" : "flex-start",
          width: fullWidth ? "100%" : "fit-content",
          maxWidth: "100%",
          boxSizing: "border-box",
          p: 0.5,
          gap: 0.5,
          borderRadius: VIRA_CARD_RADIUS,
          border: `1px solid ${border}`,
          backgroundColor: soft,
        }
      }}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <ButtonBase
            key={option.value}
            aria-pressed={selected}
            disabled={option.disabled}
            onClick={() => onChange?.(option.value)}
            sx={composeSx((theme) => {
              const ink = theme.palette.vira?.ink ?? colors.vira.ink
              const muted = theme.palette.vira?.muted ?? colors.vira.muted
              const activeBg =
                theme.palette.vira?.navActiveBg ?? colors.vira.navActiveBg
              const paper =
                theme.palette.background?.paper ?? colors.neutral.white
              return {
                flex: fullWidth ? "1 1 0" : "0 0 auto",
                minWidth: 0,
                minHeight: 40,
                px: 2,
                py: 1,
                borderRadius: "8px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: selected ? paper : "transparent",
                boxShadow: selected
                  ? `inset 0 0 0 1px ${theme.palette.vira?.border ?? colors.vira.border}`
                  : "none",
                color: selected ? ink : muted,
                opacity: option.disabled ? 0.45 : 1,
                "&:hover": {
                  backgroundColor: selected ? paper : activeBg,
                },
              }
            })}
          >
            <Stack
              component="span"
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: "100%",
                fontFamily: typography.fontFamily.display,
                fontSize: typeScale.body,
                fontWeight: selected ? 600 : 500,
                letterSpacing: "-0.01em",
                lineHeight: 1.3,
                textTransform: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {option.label}
            </Stack>
          </ButtonBase>
        )
      })}
    </Box>
  )
}
